import { useState } from "react";
import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, CardHeader, Pill, Button, Tabs, formatPnl, pnlColor } from "../components/ui";
import { SkeletonCards, SortTh, sortRows, downloadCSV, EmptyIcon } from "../components/shared";

const PAGE = 50;

export default function TradeJournal() {
  const { data: trades, error, loading } = useApi("/trades?limit=500", 5000);
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState({ key: null, asc: false });
  const [page, setPage] = useState(0);

  if (loading) return <SkeletonCards n={6} />;
  if (error) return <Card style={{ padding: 32, textAlign: "center" }}><div style={{ color: theme.redText }}>{error}</div></Card>;

  const all = trades?.trades || [];
  const exits = all.filter(t => t.action === "exit");
  const filtered = filter === "All" ? all : filter === "Entries" ? all.filter(t => t.action === "entry") : filter === "Wins" ? exits.filter(t => (t.pnl || 0) > 0) : exits.filter(t => (t.pnl || 0) < 0);
  const wins = exits.filter(t => (t.pnl || 0) > 0);
  const losses = exits.filter(t => (t.pnl || 0) < 0);
  const totalPnl = exits.reduce((s, t) => s + (t.pnl || 0), 0);
  const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
  const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0;
  const pf = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;

  const sorted = sortRows(filtered, sort);
  const paged = sorted.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.ceil(sorted.length / PAGE);

  return <>
    {/* Stats */}
    <div className="sl-grid-6" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 24 }}>
      {[
        { l: "Total", v: all.length },
        { l: "Exits", v: exits.length },
        { l: "Win Rate", v: exits.length ? `${((wins.length / exits.length) * 100).toFixed(1)}%` : "0%", c: wins.length / Math.max(exits.length, 1) > 0.55 ? theme.greenText : theme.text },
        { l: "Profit Factor", v: pf.toFixed(2), c: pf > 1.3 ? theme.greenText : theme.text },
        { l: "Net P&L", v: formatPnl(totalPnl), c: pnlColor(totalPnl) },
        { l: "Avg Win / Loss", v: `${formatPnl(avgWin)} / ${formatPnl(avgLoss)}` },
      ].map((m, i) => (
        <Card key={i} className="sl-card sl-fadein" style={{ padding: "16px 18px", textAlign: "center" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 7 }}>{m.l}</div>
          <div style={{ fontSize: 20, fontWeight: 900, fontFamily: fonts.mono, color: m.c || theme.text }}>{m.v}</div>
        </Card>
      ))}
    </div>

    {/* Trades Table */}
    <Card className="sl-fadein">
      <CardHeader title="Trade Log" sub={`${filtered.length} trades${totalPages > 1 ? ` \u00B7 Page ${page + 1}/${totalPages}` : ""}`} right={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Tabs tabs={["All", "Entries", "Wins", "Losses"]} active={filter} onChange={f => { setFilter(f); setPage(0); }} />
          <Button className="sl-btn" onClick={() => downloadCSV(filtered, `trades_${filter.toLowerCase()}.csv`)}>CSV</Button>
        </div>
      } />
      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><EmptyIcon type="list" /></div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.text3 }}>Keine Trades in diesem Filter</div>
        </div>
      ) : (
        <div style={{ overflow: "auto", maxHeight: 600 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${theme.divider}` }}>
                <SortTh sortKey="timestamp" sort={sort} setSort={setSort}>Time</SortTh>
                <th style={{ padding: "9px 14px", fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", textAlign: "left" }}>Strategy</th>
                <th style={{ padding: "9px 14px", fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", textAlign: "left" }}>Action</th>
                <th style={{ padding: "9px 14px", fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", textAlign: "left" }}>Side</th>
                <SortTh sortKey="quantity" sort={sort} setSort={setSort}>Qty</SortTh>
                <SortTh sortKey="price" sort={sort} setSort={setSort}>Price</SortTh>
                <SortTh sortKey="pnl" sort={sort} setSort={setSort} style={{ textAlign: "right" }}>P&L</SortTh>
                <th style={{ padding: "9px 14px", fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", textAlign: "left" }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((t, i) => {
                const w = (t.pnl || 0) >= 0;
                const time = t.timestamp ? new Date(t.timestamp) : null;
                return (
                  <tr key={page * PAGE + i} className="sl-row" style={{ borderBottom: `1px solid ${theme.divider}` }}>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text3 }}>
                      {time ? time.toLocaleString("de-DE", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "\u2014"}
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 700, fontFamily: fonts.mono }}>{t.strategy || "\u2014"}</td>
                    <td style={{ padding: "9px 14px" }}>
                      <Pill bg={t.action === "entry" ? theme.blueLight : w ? theme.greenLight : theme.redLight} color={t.action === "entry" ? theme.blueText : w ? theme.greenText : theme.redText}>{t.action || "\u2014"}</Pill>
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text2 }}>{t.side || "\u2014"}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text2 }}>{t.quantity || "\u2014"}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: fonts.mono, color: theme.text2 }}>{t.price ? Number(t.price).toFixed(2) : "\u2014"}</td>
                    <td style={{ padding: "9px 14px", fontSize: 13, fontWeight: 800, fontFamily: fonts.mono, color: t.action === "exit" ? pnlColor(t.pnl) : theme.text3, textAlign: "right" }}>
                      {t.action === "exit" ? formatPnl(t.pnl) : "\u2014"}
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 11, color: theme.text4 }}>{t.duration || "\u2014"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "12px 0", borderTop: `1px solid ${theme.divider}` }}>
          <Button className="sl-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</Button>
          <span style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.mono }}>Page {page + 1} of {totalPages}</span>
          <Button className="sl-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</Button>
        </div>
      )}
    </Card>
  </>;
}
